
import { defineComponent } from 'vue';
import { mapGetters, mapState } from 'vuex';
import { IMessagesState } from '@/store/modules/messages';
import LinkRecipientDialog from '@/views/_messages/components/LinkRecipientDialog.vue';
import Pagination from '@/components/Pagination/index.vue';
import { ComponentsMessagesTable_Messages, ComponentsMessagesTable_MessagesQueryVariables, ComponentsMessagesTable_MessagesTotal, ComponentsMessagesTable_MessagesTotalQueryVariables, Risolto_Channel_Enum, Risolto_View_Action_And_Message_Bool_Exp } from '@/generated/graphql';
import Logging from '@/logging';
import { messageLinkRecipient, messageArchive, messageUnarchive, messageUnlinkRecipient, messageAssign, messageUnassign } from '@/api/message';
import { GET_ACTION } from '@/gql';
import axios from 'axios';
import gql from 'graphql-tag';
import InterchangeLinks from '@/components/_Invoice/InterchangeLinks.vue';
import SendNewEmail from './SendNewEmail.vue';
import { EventBus } from '@/eventBus';
import TooltipTag from '@/components/TooltipTag.vue';
import { TABLE_COLUMN_WIDTHS } from '@/constants/table-column-widths';

const getLegalEntity = gql`
	query get_legal_entity($participant: uuid!, $recipient: uuid!) {
		risolto_recipient(
			where: { 
				participant: { _eq: $participant },
				reference: { _eq: $recipient }
			}
		) {
			legal_entity
			account_overview: processing_details(path: "account_overview")
		}
	}
`;

export default defineComponent({
	name: 'MessagesTable',
	components: {
		LinkRecipientDialog,
		Pagination,
		InterchangeLinks,
		SendNewEmail,
		TooltipTag
	},
	props: {
		showMessageTypeFilter: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			messages: [],
			labels: [],
			selectedMessages: [],
			total: -1,
			loading: false,
			sendAccountOverviewActionEmailLoading: false,
			query_id: '',
			showPopup: false,
			page: 1,
			tableColumnWidths: TABLE_COLUMN_WIDTHS
		};
	},
	computed: {
		...mapGetters(['selectedParticipantStore', 'selectedLegalEntityStore', 'participantUsers']),
		...mapState('messages', {
			orderBy: (state: IMessagesState) => state.orderBy,
			messageType: (state: IMessagesState) => state.messageType,
			filterRecipient: (state: IMessagesState) => state.filterRecipient,
			// Gets reset on participant or entity change in store/index.js
			filterAssignee: (state: IMessagesState) => state.filterAssignee,
			filterLabel: (state: IMessagesState) => state.filterLabel,
			filterSearch: (state: IMessagesState) => state.filterSearch,
			filterLinked: (state: IMessagesState) => state.filterLinked
		}),
		filterDateRange: {
			get() {
				return this.$store.state.messages.filterDateRange;
			},
			set(value) {
				this.$store.commit('messages/SET_FILTER_DATE_RANGE', value);
			}
		},
		limit: {
			get() {
				return this.$store.state.messages.limit;
			},
			set(value: number) {
				this.$store.commit('messages/SET_LIMIT', value);
			}
		},
		searchTerm() {
			const searchTerm = this.filterSearch?.trim();
			if (!searchTerm) {
				return undefined;
			}
			return searchTerm;
		}
	},
	watch: {
		messageType(newType, oldType) {
			// Auto-set date range for archive view (past week) only if no date range is already set
			if (newType === 'ARCHIVE' && oldType !== 'ARCHIVE') {
				// Only set past week if no date range is currently set
				if (!this.filterDateRange || this.filterDateRange.length === 0) {
					const oneWeekAgo = new Date();
					oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
					const today = new Date();

					this.$store.commit('messages/SET_FILTER_DATE_RANGE', [
						oneWeekAgo.toISOString().split('T')[0],
						today.toISOString().split('T')[0]
					]);
				}
			} else if (oldType === 'ARCHIVE' && newType !== 'ARCHIVE') {
				// Clear date range when switching away from archive (unless user manually set it)
				// Only clear if the current date range is exactly the past week (auto-set)
				const currentRange = this.filterDateRange;
				if (currentRange && currentRange.length === 2) {
					const oneWeekAgo = new Date();
					oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
					const today = new Date();

					const expectedStart = oneWeekAgo.toISOString().split('T')[0];
					const expectedEnd = today.toISOString().split('T')[0];

					if (currentRange[0] === expectedStart && currentRange[1] === expectedEnd) {
						this.$store.commit('messages/SET_FILTER_DATE_RANGE', []);
					}
				}
			}

			this.getMessages();
		},
		'$store.state.apollo.risolto_message': function() {
			this.getMessages();
		}
	},
	created() {
		if (this.selectedParticipantStore) {
			this.getMessages();
		}
		// subscribe to events
		EventBus.$on('paginationSizeChange', () => {
			console.log('paginationSizeChange');
			this.page = 1;
		});
	},
	methods: {
		handlePageReset() {
			console.log('handlePageReset');
			this.page = 1;
		},
		togglePopup() {
			this.showPopup = !this.showPopup;
		},
		hasAttachments(row: any): boolean {
			if (row.message_by_reference?.message_attachments?.length > 0) {
				return true;
			}
			if (row.action_by_reference?.metadata?.attachments?.length > 0) {
				return true;
			}
			if (row.action_by_reference?.metadata?.channels?.[0]?.attachments?.length > 0) {
				return true;
			}

			return false;
		},
		handleSelectionChange(selection: any[]) {
			this.selectedMessages = selection;
		},

		handleFilterAssigneeChange(value: string[]) {
			this.$store.commit('messages/SET_FILTER_ASSIGNEE', value);
		},

		handleFilterLabelChange(value: string[]) {
			this.$store.commit('messages/SET_FILTER_LABEL', value);
		},

		handleFilterLinkedChange(value: string[]) {
			this.$store.commit('messages/SET_FILTER_LINKED', value);
		},

		handleFilterDateRangeChange(value) {
			console.log('handleFilterDateRangeChange');
			console.log(value);
			this.$store.commit('messages/SET_FILTER_DATE_RANGE', value);
		},

		handleApplyFilter() {
			this.getMessages();
		},

		handleSearchChange(value: string) {
			this.$store.commit('messages/SET_FILTER_SEARCH', value);
		},

		handleSearch() {
			this.getMessages();
		},

		handleReset() {
			this.$store.commit('messages/RESET_FILTERS');
			this.getMessages();
		},

		async handleAssignTheLot(assignee: string) {
			await messageAssign(
				this.$store.state.risolto.selectedParticipantStore,
				this.selectedMessages.map((message: any) => message.message),
				assignee
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		async handleUnassign(message: string, assignee: string) {
			// TODO - Provide visual feedback that the message is being unassigned
			await messageUnassign(
				this.$store.state.risolto.selectedParticipantStore,
				message,
				assignee
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		handleLabelTheLot(label: string) {
			// TODO
		},

		async handleNewMessage() {
			this.sendAccountOverviewActionEmailLoading = true;
			try {
				await this.handleSendAccountOverview();
			} finally {
				this.sendAccountOverviewActionEmailLoading = false;
			}
		},

		async handleSendAccountOverview() {
			const legal_entity_result = await this.$apollo.query({
				query: getLegalEntity,
				variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					recipient: this.filterRecipient
				}
			});

			const enabledDamageClauses = legal_entity_result?.account_overview?.enabled_damage_clauses || false;
			const enabledReminderCosts = legal_entity_result?.account_overview?.enabled_reminder_costs || false;

			const payload = {
				participant: this.$store.state.risolto.selectedParticipantStore,
				legal_entity: legal_entity_result.data.risolto_recipient[0].legal_entity,
				recipient: this.filterRecipient,
				channel: Risolto_Channel_Enum.Email,
				enabled_damage_clauses: enabledDamageClauses,
				enabled_reminder_costs: enabledReminderCosts
			};

			const response = await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/action/create-account-overview`, payload, {
				headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
			});
			const result = await this.$apollo.query({
				query: GET_ACTION,
				variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					action: response.data.action
				}
			});
			const action = result.data.action[0];

			this.$store.commit('actions/UPSERT_ACTION', action);
			this.$store.commit('actions/SET_SELECTED_ACTION_REF', action.reference);
			this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference);
			this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel);
			this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);

			this.handleClose();
		},

		async handleLinkMessages() {
			const selection = await this.$refs.linkRecipientDialog.show();
			if (selection.length === 0) {
				return;
			}

			await messageLinkRecipient(
				this.$store.state.risolto.selectedParticipantStore,
				this.$store.state.risolto.selectedLegalEntityStore,
				this.selectedMessages.map((message: any) => message.message),
				selection
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		async handleUnlinkMessages() {
			const selection = this.selectedMessages;
			if (selection.length === 0) {
				return;
			}

			await messageUnlinkRecipient(
				this.$store.state.risolto.selectedParticipantStore,
				this.$store.state.risolto.selectedLegalEntityStore,
				selection.map((message: any) => message.message),
				[this.filterRecipient]
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		async handleArchiveTheLot() {
			await messageArchive(
				this.$store.state.risolto.loggedInUser.reference,
				this.$store.state.risolto.selectedParticipantStore,
				this.selectedMessages.map((message: any) => message.message)
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		async handleUnarchiveTheLot() {
			await messageUnarchive(
				this.$store.state.risolto.selectedParticipantStore,
				this.selectedMessages.map((message: any) => message.message)
			);
			this.$store.commit('apollo/RISOLTO_MESSAGE');
		},

		handleRowClick(row: any) {
			if (row.message) {
				this.$store.commit('risolto/SET_EMAIL_DETAIL_DIALOG_MESSAGE', row.message);
				this.$store.commit('risolto/SET_EMAIL_DETAIL_DIALOG_VISIBLE', true);
			} else if (row.action) {
				this.$store.commit('actions/UPSERT_ACTION', row.action_by_reference);
				this.$store.commit('actions/SET_SELECTED_ACTION_REF', row.action_by_reference.reference);
				this.$store.commit('risolto/SET_SELECTED_RECIPIENT', row.action_by_reference.recipient_by_reference);
				this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', row.action_by_reference.channel);
				this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);
			}
		},

		async getMessages() {
			if (this.loading) {
				return;
			}

			this.loading = true;

			try {
				const current_query_id = (Math.random() + 1).toString(36).substring(7);
				this.query_id = current_query_id;

				// 1) search term
				const p_search_term =
				(this.searchTerm && this.searchTerm.trim().length > 0) ? this.searchTerm.trim() : null;

				// 2) recipient
				const p_recipient = this.filterRecipient ?? null;

				// 3) is_linked (array → tri-state boolean)
				const hasTrue = this.filterLinked?.includes(true);
				const hasFalse = this.filterLinked?.includes(false);
				const p_is_linked =
				(hasTrue && hasFalse) ? null : (hasTrue ? true : (hasFalse ? false : null));

				// 4) assignees (_uuid in GraphQL)
				let p_assignees: string | null = null;
				if (this.filterAssignee && this.filterAssignee.length > 0 && !this.filterAssignee.includes('UNASSIGNED')) {
					p_assignees = `{${this.filterAssignee.join(',')}}`;
				}

				// 5) message type → direction + archived flag (+ optional cutoff)
				let p_direction: string | null = null;
				let p_archived_is_null: boolean | null = null;
				let p_date_end: string | null = null;

				switch (this.messageType) {
					case 'IN':
						p_direction = 'IN';
						p_archived_is_null = true;
						break;
					case 'OUT':
						p_direction = 'OUT';
						p_archived_is_null = true;
						p_date_end = new Date().toISOString();
						break;
					case 'ARCHIVE':
						p_archived_is_null = false;
						p_date_end = new Date().toISOString();
						break;
					case 'ALL':
					default:
						// leave nulls
						break;
				}

				// 6) date range override (if user picked a range)
				let p_date_start: string | null = null;
				if (this.filterDateRange) {
					[p_date_start, p_date_end] = [
						this.filterDateRange[0] ?? null,
						this.filterDateRange[1] ?? null
					];
				}

				// 7) legal entity
				const p_legal_entity = this.selectedLegalEntityStore ?? null;

				// 8) participant
				const p_participant = this.selectedParticipantStore;

				let totalVariables: ComponentsMessagesTable_MessagesTotalQueryVariables = {
					participant: p_participant,
					legalEntity: p_legal_entity,
					recipient: p_recipient,
					assignees: p_assignees,
					dateStart: p_date_start,
					dateEnd: p_date_end,
					archivedIsNull: p_archived_is_null,
					direction: p_direction,
					isLinked: p_is_linked,
					searchTerm: p_search_term
				};

				this.$apollo.query({
					query: ComponentsMessagesTable_MessagesTotal,
					variables: totalVariables
				}).then(result => {
					if (current_query_id === this.query_id && result.data) {
						this.total = result.data.total.aggregate.count;
					}
				});

				let variables: ComponentsMessagesTable_MessagesQueryVariables = {
					participant: p_participant,
					limit: this.limit,
					offset: (this.page - 1) * this.limit,
					orderBy: this.orderBy,
					legalEntity: p_legal_entity,
					recipient: p_recipient,
					assignees: p_assignees,
					dateStart: p_date_start,
					dateEnd: p_date_end,
					archivedIsNull: p_archived_is_null,
					direction: p_direction,
					isLinked: p_is_linked,
					searchTerm: p_search_term
				};

				let result = await this.$apollo.query({
					query: ComponentsMessagesTable_Messages,
					variables: variables
				});

				if (result.data) {
					this.messages = result.data.messages;
				}
			} catch (e) {
				Logging.error(e);
			}

			this.loading = false;
		}
	}
});
